/**
 * A* de grilla GRUESA para el World Map (tecla M): calcula una ruta caminable
 * aproximada desde el player hasta el destino de un objetivo de quest, para
 * dibujar el "camino" (breadcrumb) sobre el mapa.
 *
 * ¿Por qué gruesa? El mundo es 1024×1024 = ~1M tiles; un A* fino podría explorar
 * cientos de miles de nodos en terreno abierto. Agrupamos en bloques de
 * `BLOCK` tiles (default 4) → el peor caso baja a 256×256 = ~65k nodos. Un bloque
 * es caminable si ALGÚN tile interno lo es (no sella corredores de 1 tile).
 *
 * `isBlocked(tileX, tileY)` la provee el caller (cliente) combinando
 * `GameScene.blockedTiles` (+ agua como bloqueo para ruta terrestre). Si no hay
 * ruta o se supera el cap de nodos, devuelve `null` y la UI cae a una línea recta.
 *
 * Devuelve waypoints en coords de TILE (centros de bloque), del start al goal.
 * Ver docs/WORLD_MAP.md.
 */

const BLOCK = 4;
const MAX_EXPANSIONS = 60_000; // cap de seguridad (nodos sacados de la cola)

type BlockKey = number; // by * COLS + bx

/** Min-heap binario simple (nodo = blockKey; prioridad = f). */
class MinHeap {
  private keys: number[] = [];
  private prio: number[] = [];

  get size(): number {
    return this.keys.length;
  }

  push(key: number, f: number): void {
    this.keys.push(key);
    this.prio.push(f);
    let i = this.keys.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.prio[parent] <= this.prio[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number {
    const topKey = this.keys[0];
    const lastKey = this.keys.pop()!;
    const lastPrio = this.prio.pop()!;
    if (this.keys.length > 0) {
      this.keys[0] = lastKey;
      this.prio[0] = lastPrio;
      let i = 0;
      const n = this.keys.length;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let smallest = i;
        if (l < n && this.prio[l] < this.prio[smallest]) smallest = l;
        if (r < n && this.prio[r] < this.prio[smallest]) smallest = r;
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return topKey;
  }

  private swap(a: number, b: number): void {
    const k = this.keys[a];
    this.keys[a] = this.keys[b];
    this.keys[b] = k;
    const p = this.prio[a];
    this.prio[a] = this.prio[b];
    this.prio[b] = p;
  }
}

export interface Tile {
  x: number;
  y: number;
}

export interface CoarseRouteOptions {
  /** Lado del mundo en tiles (default: se infiere del máximo entre start/goal + margen). */
  worldTiles: number;
  isBlocked: (tileX: number, tileY: number) => boolean;
  block?: number;
}

/**
 * Ruta gruesa start→goal. Devuelve centros de bloque (coords de tile) o null si
 * no hay ruta / se excede el presupuesto de exploración.
 */
export function findCoarseRoute(
  start: Tile,
  goal: Tile,
  opts: CoarseRouteOptions
): Tile[] | null {
  const B = opts.block ?? BLOCK;
  const COLS = Math.ceil(opts.worldTiles / B);
  const ROWS = COLS;

  const toBlock = (t: Tile): { bx: number; by: number } => ({
    bx: Math.floor(t.x / B),
    by: Math.floor(t.y / B),
  });
  const keyOf = (bx: number, by: number): BlockKey => by * COLS + bx;
  const centerTile = (bx: number, by: number): Tile => ({
    x: Math.min(opts.worldTiles - 1, bx * B + (B >> 1)),
    y: Math.min(opts.worldTiles - 1, by * B + (B >> 1)),
  });

  // Memo de caminabilidad por bloque (any tile libre → caminable).
  const walkableMemo = new Map<BlockKey, boolean>();
  const isBlockWalkable = (bx: number, by: number): boolean => {
    if (bx < 0 || by < 0 || bx >= COLS || by >= ROWS) return false;
    const k = keyOf(bx, by);
    const memo = walkableMemo.get(k);
    if (memo !== undefined) return memo;
    let walkable = false;
    const x0 = bx * B;
    const y0 = by * B;
    for (let dy = 0; dy < B && !walkable; dy++) {
      for (let dx = 0; dx < B; dx++) {
        const tx = x0 + dx;
        const ty = y0 + dy;
        if (tx >= opts.worldTiles || ty >= opts.worldTiles) continue;
        if (!opts.isBlocked(tx, ty)) {
          walkable = true;
          break;
        }
      }
    }
    walkableMemo.set(k, walkable);
    return walkable;
  };

  const startB = toBlock(start);
  const goalB = toBlock(goal);
  const goalKey = keyOf(goalB.bx, goalB.by);

  // Heurística octile (permite diagonales).
  const heur = (bx: number, by: number): number => {
    const dx = Math.abs(bx - goalB.bx);
    const dy = Math.abs(by - goalB.by);
    return (dx + dy) + (Math.SQRT2 - 2) * Math.min(dx, dy);
  };

  const gScore = new Map<BlockKey, number>();
  const cameFrom = new Map<BlockKey, BlockKey>();
  const open = new MinHeap();

  const startKey = keyOf(startB.bx, startB.by);
  gScore.set(startKey, 0);
  open.push(startKey, heur(startB.bx, startB.by));

  const DIRS = [
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];

  let expansions = 0;
  while (open.size > 0) {
    if (++expansions > MAX_EXPANSIONS) return null;
    const current = open.pop();
    if (current === goalKey) {
      return reconstruct(cameFrom, current, COLS, centerTile);
    }
    const cbx = current % COLS;
    const cby = Math.floor(current / COLS);
    const cg = gScore.get(current) ?? Infinity;

    for (const [dx, dy] of DIRS) {
      const nbx = cbx + dx;
      const nby = cby + dy;
      if (!isBlockWalkable(nbx, nby)) continue;
      // No cortar esquinas por diagonal si ambos ortogonales están bloqueados.
      if (dx !== 0 && dy !== 0) {
        if (!isBlockWalkable(cbx + dx, cby) && !isBlockWalkable(cbx, cby + dy)) continue;
      }
      const step = dx !== 0 && dy !== 0 ? Math.SQRT2 : 1;
      const nKey = keyOf(nbx, nby);
      const tentative = cg + step;
      if (tentative < (gScore.get(nKey) ?? Infinity)) {
        cameFrom.set(nKey, current);
        gScore.set(nKey, tentative);
        open.push(nKey, tentative + heur(nbx, nby));
      }
    }
  }
  return null;
}

function reconstruct(
  cameFrom: Map<BlockKey, BlockKey>,
  goalKey: BlockKey,
  COLS: number,
  centerTile: (bx: number, by: number) => Tile
): Tile[] {
  const path: Tile[] = [];
  let cur: BlockKey | undefined = goalKey;
  while (cur !== undefined) {
    const bx = cur % COLS;
    const by = Math.floor(cur / COLS);
    path.push(centerTile(bx, by));
    cur = cameFrom.get(cur);
  }
  path.reverse();
  return path;
}
